import { useMemo, useRef, useState } from "react";
import type { CourtType, Play, PlaySummary, Role } from "../types";
import { CourtBoard } from "./CourtBoard";
import { CirclePlay, Copy, Download, FileUp, LogOut, Pencil, Plus, Search, Trash2 } from "./icons";

interface LibraryPageProps {
  role: Role;
  plays: PlaySummary[];
  drafts: Play[];
  loading: boolean;
  error: string;
  onOpen: (id: string, edit: boolean) => void;
  onResumeDraft: (play: Play) => void;
  onDeleteDraft: (id: string) => void;
  onCreate: (courtType: CourtType) => void;
  onCopy: (id: string) => void;
  onDelete: (id: string) => void;
  onShare: (id: string) => void;
  onExport: () => void;
  onImport: (file: File) => void;
  onLogout: () => void;
  onRetry: () => void;
}

function formatDate(value: string) {
  const date = new Date(value);
  return new Intl.DateTimeFormat("zh-CN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(date);
}

export function LibraryPage({
  role, plays, drafts, loading, error, onOpen, onResumeDraft, onDeleteDraft, onCreate, onCopy, onDelete, onShare, onExport, onImport, onLogout, onRetry,
}: LibraryPageProps) {
  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const filtered = useMemo(() => plays.filter((play) => play.name.toLowerCase().includes(query.trim().toLowerCase())), [plays, query]);

  return (
    <main className="library-page">
      <header className="library-header">
        <div className="brand-lockup"><div className="brand-mark small"><span>5</span></div><div><strong>战术板</strong><span>球队共享战术库</span></div></div>
        <div className="library-header-actions">
          <span className={`role-badge ${role}`}>{role === "coach" ? "教练管理" : "队员只读"}</span>
          <button className="icon-button" onClick={onLogout} aria-label="退出登录"><LogOut size={18} /></button>
        </div>
      </header>

      <section className="library-hero">
        <div><p className="eyebrow">TEAM PLAYBOOK</p><h1>战术库</h1><p>{role === "coach" ? "把意图画清楚，让全队按同一节奏行动。" : "看懂站位、跑动与传球，带着答案上场。"}</p></div>
        {role === "coach" && <button className="primary large" onClick={() => setShowCreate(true)}><Plus size={19} /> 新建战术</button>}
      </section>

      <section className="library-toolbar">
        <label className="search-box"><Search size={18} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索战术名称" /></label>
        {role === "coach" && <div className="backup-actions">
          <button className="secondary" onClick={onExport}><Download size={16} /> 导出备份</button>
          <button className="secondary" onClick={() => inputRef.current?.click()}><FileUp size={16} /> 导入</button>
          <input ref={inputRef} type="file" accept="application/json,.json" hidden onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onImport(file);
            event.target.value = "";
          }} />
        </div>}
      </section>

      {error && <div className="load-error" role="alert"><span>{error}</span><button onClick={onRetry}>重新加载</button></div>}
      {role === "coach" && drafts.length > 0 && (
        <section className="draft-section" aria-labelledby="draft-heading">
          <div className="draft-heading"><div><p className="eyebrow">LOCAL DRAFTS</p><h2 id="draft-heading">本机草稿</h2></div><span>仅保存在这台设备</span></div>
          <div className="draft-list">
            {drafts.map((draft) => (
              <article key={draft.id} className="draft-card">
                <div><strong>{draft.name || "未命名战术"}</strong><span>{draft.steps.length} 步 · {draft.version === 0 ? "尚未保存" : `基于 v${draft.version}`}</span></div>
                <button className="secondary" onClick={() => onResumeDraft(draft)}><Pencil size={15} /> 继续编辑</button>
                <button className="icon-button danger" onClick={() => onDeleteDraft(draft.id)} aria-label={`删除草稿 ${draft.name}`}><Trash2 size={16} /></button>
              </article>
            ))}
          </div>
        </section>
      )}
      {loading ? <div className="loading-grid"><span /><span /><span /></div> : filtered.length ? (
        <section className="play-grid">
          {filtered.map((play) => (
            <article className="play-card" key={play.id}>
              <button className="play-thumbnail" onClick={() => onOpen(play.id, false)} aria-label={`打开 ${play.name}`}>
                <CourtBoard step={play.firstStep} courtType={play.courtType} compact />
                <span className="thumbnail-play"><CirclePlay size={32} /></span>
                <span className="court-badge">{play.courtType === "half" ? "半场" : "全场"}</span>
              </button>
              <div className="play-card-body">
                <div><h2>{play.name}</h2><p>{play.description || "暂无战术说明"}</p></div>
                <div className="play-meta"><span>更新于 {formatDate(play.updatedAt)}</span><span>v{play.version}</span></div>
                <div className="card-actions">
                  <button className="secondary" onClick={() => onOpen(play.id, role === "coach")}>
                    {role === "coach" ? <><Pencil size={15} /> 编辑</> : <><CirclePlay size={15} /> 查看</>}
                  </button>
                  <button className="icon-button" onClick={() => onShare(play.id)} aria-label="复制分享链接"><Copy size={16} /></button>
                  {role === "coach" && <>
                    <button className="icon-button" onClick={() => onCopy(play.id)} aria-label="复制战术"><Copy size={16} /></button>
                    <button className="icon-button danger" onClick={() => onDelete(play.id)} aria-label="删除战术"><Trash2 size={16} /></button>
                  </>}
                </div>
              </div>
            </article>
          ))}
        </section>
      ) : (
        <section className="empty-state">
          <div className="empty-ball">5</div>
          <h2>{query ? "没有匹配的战术" : "战术库还是空的"}</h2>
          <p>{query ? "换一个关键词试试。" : role === "coach" ? "从一套最常用的战术开始。" : "教练保存战术后会显示在这里。"}</p>
          {role === "coach" && !query && <button className="primary" onClick={() => setShowCreate(true)}><Plus size={17} /> 创建第一套战术</button>}
        </section>
      )}

      {showCreate && <div className="modal-backdrop" onMouseDown={() => setShowCreate(false)}>
        <section className="modal" role="dialog" aria-modal="true" aria-labelledby="new-play-title" onMouseDown={(event) => event.stopPropagation()}>
          <p className="eyebrow">NEW PLAY</p><h2 id="new-play-title">选择球场范围</h2><p>创建后仍可在编辑器中完善名称、说明和步骤。</p>
          <div className="court-options">
            <button onClick={() => { setShowCreate(false); onCreate("half"); }}><span className="court-option-graphic half" /><strong>半场战术</strong><small>阵地进攻、边线球</small></button>
            <button onClick={() => { setShowCreate(false); onCreate("full"); }}><span className="court-option-graphic full" /><strong>全场战术</strong><small>快攻、全场防守</small></button>
          </div>
          <button className="secondary wide" onClick={() => setShowCreate(false)}>取消</button>
        </section>
      </div>}
    </main>
  );
}
